import { CHART_SEGMENT_COLORS } from './chartPalette';

const DATE_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Canonical cohort display order (legend, grouped bars, tooltip). */
export const USER_COHORT_CHART_SERIES = [
  { dataKey: 'reviewers', label: 'Reviewers' },
  { dataKey: 'drinkers', label: 'Drinkers' },
  { dataKey: 'explorers', label: 'Explorers' },
  { dataKey: 'inactive', label: 'Inactive' },
];

/**
 * @param {string | undefined} dataKey
 * @returns {number}
 */
export function getUserCohortSeriesSortIndex(dataKey) {
  const seriesIndex = USER_COHORT_CHART_SERIES.findIndex(
    (seriesConfig) => seriesConfig.dataKey === dataKey,
  );
  return seriesIndex >= 0 ? seriesIndex : USER_COHORT_CHART_SERIES.length;
}

/**
 * Recharts `itemSorter` uses lodash sortBy(iteratee) — one payload entry in, sort key out.
 *
 * @param {{ dataKey?: string } | null | undefined} payloadEntry
 * @returns {number}
 */
export function getUserCohortSeriesItemSortKey(payloadEntry) {
  if (payloadEntry == null || typeof payloadEntry !== 'object') {
    return USER_COHORT_CHART_SERIES.length;
  }
  const dataKey = payloadEntry.dataKey;
  return getUserCohortSeriesSortIndex(typeof dataKey === 'string' ? dataKey : undefined);
}

const SERIES_COLOR_BY_DATA_KEY = USER_COHORT_CHART_SERIES.reduce(
  (colorMap, seriesConfig, seriesIndex) => {
    colorMap[seriesConfig.dataKey] =
      CHART_SEGMENT_COLORS[seriesIndex % CHART_SEGMENT_COLORS.length];
    return colorMap;
  },
  {},
);

/**
 * @param {string} dateKey YYYY-MM-DD
 * @returns {string}
 */
export function formatUserCohortDateLabelShort(dateKey) {
  if (typeof dateKey !== 'string' || !DATE_KEY_REGEX.test(dateKey)) {
    return '';
  }
  return dateKey.slice(5);
}

/**
 * @param {string} dateKey
 * @returns {boolean}
 */
export function isValidUserCohortDateKey(dateKey) {
  return typeof dateKey === 'string' && DATE_KEY_REGEX.test(dateKey);
}

/**
 * @param {Record<string, unknown>} metrics
 * @returns {Record<string, number>}
 */
function normalizeUserCohortMetrics(metrics) {
  const normalized = {};
  USER_COHORT_CHART_SERIES.forEach((seriesConfig) => {
    const rawValue = metrics[seriesConfig.dataKey];
    const parsed = Number(rawValue);
    normalized[seriesConfig.dataKey] =
      Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;
  });
  return normalized;
}

/**
 * @param {Array<{ id?: string, date?: string, metrics?: Record<string, unknown> }>} reportDocuments
 * @returns {Array<{ dateKey: string, dateLabelShort: string, reviewers: number, drinkers: number, explorers: number, inactive: number }>}
 */
export function buildUserCohortDailyChartRowsAsc(reportDocuments) {
  const rows = (Array.isArray(reportDocuments) ? reportDocuments : [])
    .map((documentRow) => {
      const dateKey = typeof documentRow.date === 'string' ? documentRow.date : '';
      const metrics =
        typeof documentRow.metrics === 'object' && documentRow.metrics !== null
          ? documentRow.metrics
          : {};
      return {
        dateKey,
        dateLabelShort: formatUserCohortDateLabelShort(dateKey),
        ...normalizeUserCohortMetrics(metrics),
      };
    })
    .filter((row) => isValidUserCohortDateKey(row.dateKey));

  rows.sort((rowA, rowB) => rowA.dateKey.localeCompare(rowB.dateKey));
  return rows;
}

/**
 * @param {string} dataKey
 * @returns {string | undefined}
 */
export function getUserCohortSeriesColor(dataKey) {
  return SERIES_COLOR_BY_DATA_KEY[dataKey];
}
